import { AdminScreenView } from "../components/AdminScreenView";
import { TextInputLabel } from "../components/TextInputLabel";
import {
  Alert,
  ButtonRectangle,
  displayUnexpectedErrorAndLog,
  FlexedSpinner,
  SetWaitingHandler,
  Spacer,
  TextInput,
  TBody,
  THeading1,
  THeading2,
  log,
} from "@eatbetter/ui-shared";
import { useCallback, useEffect, useState } from "react";
import { View } from "react-native";
import { Checkbox, Select } from "antd";
import { ActiveIssue, ServiceStatus, validateServiceStatusWithIssue } from "@eatbetter/composite-shared";
import { defaultTimeProvider } from "@eatbetter/common-shared";
import { useDispatch, ThunkAction } from "../lib/AdminRedux";

export const AdminServiceStatusScreenNav = {
  getPath: () => {
    return "/service-status";
  },
};

// Predefined statuses from the CLI
const presetStatuses: Record<string, Omit<ActiveIssue, "lastUpdate">> = {
  aiProcessing: {
    title: "AI Recipe Import",
    body: "We're experiencing some issues with AI systems and some recipes are not importing properly. We are actively monitoring the situation and will reprocess all affected recipes once it is addressed.",
  },
  aiProcessingPhotos: {
    title: "AI Recipe Import",
    body: "We're experiencing some issues with our photo import AI systems and some photo recipe imports might fail. We are actively monitoring the situation and will reprocess all affected recipes once our AI provider has stabilized.",
  },
  internetOutage: {
    title: "Widespread internet outage",
    body: "There is currently a widespread issue affecting many internet service providers. You might experience issues signing in, adding or viewing recipes, or other problems. We are monitoring the situation closely.",
  },
  instagramImports: {
    title: "Instagram Import Issue",
    body: "There is currently an issue importing Instagram Reels. We are actively investigating and we will reprocess affected recipes once this issue is resolved.",
  },
  shareExtension: {
    title: "Share to Deglaze Issue",
    body: "There is an issue with our Share to Deglaze sheet. We are working on getting a fix out ASAP - we apologize for the inconvenience.",
  },
};

const presetOptions = [
  { label: "Custom", value: "" },
  ...Object.entries(presetStatuses).map(([key, value]) => ({
    label: `${key} — ${value.title}`,
    value: key,
  })),
];

type Platform = "ios" | "android" | "web";
const allPlatforms: Platform[] = ["ios", "android", "web"];
const platformCheckboxOptions = [
  { label: "iOS", value: "ios" as Platform },
  { label: "Android", value: "android" as Platform },
  { label: "Web", value: "web" as Platform },
];

const getServiceStatus =
  (setWaiting?: SetWaitingHandler): ThunkAction<ServiceStatus> =>
  async (_dispatch, _getState, deps) => {
    const resp = await deps.api.withThrow(setWaiting).getServiceStatus();
    return resp.data;
  };

const setServiceStatus =
  (status: ServiceStatus, setWaiting?: SetWaitingHandler): ThunkAction<void> =>
  async (_dispatch, _getState, deps) => {
    await deps.api.withThrow(setWaiting).setServiceStatus(status);
  };

// Match version format: digits separated by dots, e.g. "6", "6.0", "6.0.1", "6.0.1.1234"
const versionPattern = /^\d+(\.\d+)*$/;

function platformsFromIssue(platforms?: Platform[]): Platform[] {
  if (!platforms || platforms.length === 0) return [...allPlatforms];
  return platforms;
}

function platformsToIssueValue(platforms: Platform[]): Platform[] | undefined {
  if (platforms.length === allPlatforms.length) return undefined;
  return platforms;
}

export const AdminServiceStatusScreen = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<ServiceStatus | undefined>();
  const [mode, setMode] = useState<"view" | "edit">("view");

  // Edit form state
  const [preset, setPreset] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [platforms, setPlatforms] = useState<Platform[]>([...allPlatforms]);
  const [versions, setVersions] = useState<string[]>([]);
  const [versionError, setVersionError] = useState<string | undefined>();

  const fetchStatus = useCallback(async () => {
    try {
      const status = await dispatch(getServiceStatus(setLoading));
      setCurrentStatus(status);
      if (!status.activeIssue) {
        setMode("edit");
      } else {
        setMode("view");
      }
    } catch (err) {
      displayUnexpectedErrorAndLog("Error fetching service status", err);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchStatus().catch(err => {
      log.errorCaught("Error calling fetchStatus in AdminServiceStatusScreen", err);
      alert(`${err}`);
    });
  }, []);

  const populateFormFromIssue = useCallback((issue: ActiveIssue) => {
    setTitle(issue.title);
    setBody(issue.body);
    setPlatforms(platformsFromIssue(issue.onlyForPlatform));
    setVersions(issue.onlyForAppVersion ?? []);
    setVersionError(undefined);
  }, []);

  const startEdit = useCallback(() => {
    if (currentStatus?.activeIssue) {
      populateFormFromIssue(currentStatus.activeIssue);
      // Check if it matches a preset
      const matchingPreset = Object.entries(presetStatuses).find(
        ([, v]) => v.title === currentStatus.activeIssue!.title && v.body === currentStatus.activeIssue!.body
      );
      setPreset(matchingPreset?.[0] ?? "");
    } else {
      setPreset("");
      setTitle("");
      setBody("");
      setPlatforms([...allPlatforms]);
      setVersions([]);
      setVersionError(undefined);
    }
    setMode("edit");
  }, [currentStatus, populateFormFromIssue]);

  const onPresetChange = useCallback(
    (value: string) => {
      setPreset(value);
      if (value && presetStatuses[value]) {
        const s = presetStatuses[value]!;
        setTitle(s.title);
        setBody(s.body);
      }
    },
    [setPreset, setTitle, setBody]
  );

  const onVersionsChange = useCallback((values: string[]) => {
    // Split any comma-separated entries into individual tags
    const expanded = values.flatMap(v =>
      v
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
    );
    const invalid = expanded.find(v => !versionPattern.test(v));
    if (invalid) {
      setVersionError(`Invalid version: "${invalid}". Use format like 6, 6.0, or 6.0.1`);
      return;
    }
    // Deduplicate
    const unique = [...new Set(expanded)];
    setVersionError(undefined);
    setVersions(unique);
  }, []);

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !versionError;

  const doSetStatus = useCallback(
    async (status: ServiceStatus) => {
      try {
        await dispatch(setServiceStatus(status, setSaving));
        await fetchStatus();
      } catch (err) {
        displayUnexpectedErrorAndLog("Error updating service status", err);
      }
    },
    [dispatch, fetchStatus]
  );

  const onSave = useCallback(() => {
    if (versionError) return;

    const issuePlatforms = platformsToIssueValue(platforms);
    const issue: ActiveIssue = {
      title: title.trim(),
      body: body.trim(),
      lastUpdate: defaultTimeProvider(),
      ...(issuePlatforms ? { onlyForPlatform: issuePlatforms } : {}),
      ...(versions.length > 0 ? { onlyForAppVersion: versions } : {}),
    };

    const status: ServiceStatus = { activeIssue: issue };
    const validation = validateServiceStatusWithIssue(status);
    if (!validation.success) {
      Alert.alert("Validation Error", validation.result, [{ type: "cancel", onPress: () => {} }]);
      return;
    }

    Alert.alert("Set Service Status", `Title: ${issue.title}\n\nBody: ${issue.body}`, [
      { type: "cancel", onPress: () => {} },
      { type: "save", text: "Set Status", onPress: () => doSetStatus(status) },
    ]);
  }, [title, body, platforms, versions, versionError, doSetStatus]);

  const onClear = useCallback(() => {
    Alert.alert("Clear Service Status", "This will remove the active service status message for all users.", [
      { type: "cancel", onPress: () => {} },
      { type: "delete", text: "Clear", onPress: () => doSetStatus({}) },
    ]);
  }, [doSetStatus]);

  if (loading) {
    return (
      <AdminScreenView>
        <THeading1>Service Status</THeading1>
        <Spacer vertical={2} />
        <FlexedSpinner />
      </AdminScreenView>
    );
  }

  return (
    <AdminScreenView>
      <THeading1>Service Status</THeading1>
      <Spacer vertical={2} />

      {mode === "view" && currentStatus?.activeIssue && (
        <CurrentStatusView issue={currentStatus.activeIssue} onEdit={startEdit} onClear={onClear} clearing={saving} />
      )}

      {mode === "edit" && !currentStatus?.activeIssue && (
        <>
          <TBody>There is no active service status message. Use the form below to set one.</TBody>
          <Spacer vertical={2} />
        </>
      )}

      {mode === "edit" && (
        <EditStatusView
          preset={preset}
          onPresetChange={onPresetChange}
          title={title}
          onTitleChange={setTitle}
          body={body}
          onBodyChange={setBody}
          platforms={platforms}
          onPlatformsChange={setPlatforms}
          versions={versions}
          onVersionsChange={onVersionsChange}
          versionError={versionError}
          canSubmit={canSubmit}
          saving={saving}
          onSave={onSave}
          onCancel={currentStatus?.activeIssue ? () => setMode("view") : undefined}
        />
      )}
    </AdminScreenView>
  );
};

const CurrentStatusView = (props: {
  issue: ActiveIssue;
  onEdit: () => void;
  onClear: () => void;
  clearing: boolean;
}) => {
  const { issue } = props;
  return (
    <View style={{ maxWidth: 600 }}>
      <THeading2>Active Issue</THeading2>
      <Spacer vertical={1} />
      <TextInputLabel text="Title" />
      <TBody fontWeight="heavy">{issue.title}</TBody>
      <Spacer vertical={1} />
      <TextInputLabel text="Body" />
      <TBody>{issue.body}</TBody>
      <Spacer vertical={1} />
      <TextInputLabel text="Last Updated" />
      <TBody>{new Date(issue.lastUpdate as number).toLocaleString()}</TBody>
      {issue.onlyForPlatform && issue.onlyForPlatform.length > 0 && (
        <>
          <Spacer vertical={1} />
          <TextInputLabel text="Platforms" />
          <TBody>{issue.onlyForPlatform.join(", ")}</TBody>
        </>
      )}
      {issue.onlyForAppVersion && issue.onlyForAppVersion.length > 0 && (
        <>
          <Spacer vertical={1} />
          <TextInputLabel text="App Versions" />
          <TBody>{issue.onlyForAppVersion.join(", ")}</TBody>
        </>
      )}
      <Spacer vertical={2} />
      <View style={{ flexDirection: "row", gap: 12 }}>
        <ButtonRectangle type="submit" title="Update" onPress={props.onEdit} />
        <ButtonRectangle type="cancel" title="Clear" onPress={props.onClear} waiting={props.clearing} />
      </View>
    </View>
  );
};

const EditStatusView = (props: {
  preset: string;
  onPresetChange: (value: string) => void;
  title: string;
  onTitleChange: (text: string) => void;
  body: string;
  onBodyChange: (text: string) => void;
  platforms: Platform[];
  onPlatformsChange: (values: Platform[]) => void;
  versions: string[];
  onVersionsChange: (values: string[]) => void;
  versionError: string | undefined;
  canSubmit: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: (() => void) | undefined;
}) => {
  return (
    <View style={{ maxWidth: 600 }}>
      <THeading2>Set Status</THeading2>
      <Spacer vertical={1} />

      <TextInputLabel text="Preset" />
      <Select value={props.preset} onChange={props.onPresetChange} options={presetOptions} style={{ width: "100%" }} />
      <Spacer vertical={1} />

      <TextInputLabel text={`Title (max 40 chars) — ${props.title.length}/40`} />
      <TextInput value={props.title} onChangeText={props.onTitleChange} placeholderText="Issue title" />
      <Spacer vertical={1} />

      <TextInputLabel text="Body" />
      <TextInput
        value={props.body}
        onChangeText={props.onBodyChange}
        placeholderText="Issue description"
        multiline
        minHeight={150}
      />
      <Spacer vertical={1} />

      <TextInputLabel text="Platforms" />
      <Checkbox.Group
        value={props.platforms}
        onChange={values => props.onPlatformsChange(values as Platform[])}
        options={platformCheckboxOptions}
      />
      <Spacer vertical={1} />

      <TextInputLabel text="App Versions (type a version and press Enter, e.g. 6, 6.0, 6.0.1)" />
      <Select
        mode="tags"
        value={props.versions}
        onChange={props.onVersionsChange}
        style={{ width: "100%" }}
        placeholder="Leave empty for all versions"
        tokenSeparators={[","]}
        notFoundContent={null}
      />
      {!!props.versionError && (
        <>
          <Spacer vertical={0.5} />
          <TBody color="red">{props.versionError}</TBody>
        </>
      )}
      <Spacer vertical={2} />

      <View style={{ flexDirection: "row", gap: 12 }}>
        <ButtonRectangle
          type="submit"
          title="Save"
          onPress={props.onSave}
          disabled={!props.canSubmit}
          waiting={props.saving}
        />
        {props.onCancel && <ButtonRectangle type="cancel" title="Cancel" onPress={props.onCancel} />}
      </View>
    </View>
  );
};
